import { useState, useEffect } from "react";
import { X, Gift, CheckCircle2, Loader2, AlertCircle, Coins } from "lucide-react";
import api from "../../lib/api";

export default function VoucherModal({ onClose, onSuccess }) {
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(null);

  const formatPin = (raw) => {
    const clean = raw.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 16);
    const parts = [];
    for (let i = 0; i < clean.length; i += 4) parts.push(clean.slice(i, i + 4));
    return parts.join("-");
  };

  const handleChange = (e) => {
    setPin(formatPin(e.target.value));
    setError("");
  };

  const handleRedeem = async () => {
    const clean = pin.replace(/-/g, "").toUpperCase();
    if (clean.length < 16) {
      setError("Codul PIN trebuie să aibă 16 caractere (format: XXXX-XXXX-XXXX-XXXX).");
      return;
    }
    setLoading(true);
    setError("");
    try {
      // Backend expects format IDG-XXXX-XXXX-XXXX-XXXX
      const formatted = `IDG-${pin}`.replace("IDG-IDG-", "IDG-");
      const r = await api.post("/wallet/redeem-voucher", { pin: formatted });
      if (r.data?.ok) {
        setSuccess(r.data);
        if (onSuccess) onSuccess(r.data.new_balance, r.data.coins_credited);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Cod PIN invalid sau deja utilizat.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-[#0d0f16] border border-white/15 rounded-xl shadow-[0_30px_80px_rgba(0,0,0,0.9)] overflow-hidden animate-scaleUp">
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/30 flex items-center justify-center">
              <Gift className="w-4 h-4 text-violet-400" />
            </div>
            <h2 className="font-chakra font-black text-sm uppercase tracking-widest text-white">Introdu Cod Voucher</h2>
          </div>
          <button onClick={onClose} className="w-7 h-7 rounded bg-white/5 hover:bg-rose-500/20 text-zinc-400 hover:text-rose-400 flex items-center justify-center transition-all">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6">
          {success ? (
            <div className="flex flex-col items-center gap-5 text-center py-4">
              <div className="w-16 h-16 rounded-full bg-violet-500/10 border-2 border-violet-500/40 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8 text-violet-400" />
              </div>
              <div>
                <h3 className="font-chakra font-black text-lg text-white uppercase">Voucher Revendicat!</h3>
                <p className="text-sm text-zinc-400 font-mono mt-1">{success.message}</p>
              </div>
              <div className="flex items-center gap-3 p-3 rounded-lg bg-violet-500/10 border border-violet-500/30">
                <Coins className="w-5 h-5 text-violet-400" />
                <div className="text-left">
                  <div className="text-xs text-zinc-400 font-mono">Coins adăugate</div>
                  <div className="font-mono font-black text-violet-400 text-lg">+{success.coins_credited?.toLocaleString()} IDG</div>
                </div>
              </div>
              <button onClick={onClose}
                className="px-8 py-2.5 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-black"
                style={{ background: "var(--tf-primary)" }}>
                Super!
              </button>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <p className="text-sm text-zinc-400 font-mono">Introdu codul PIN al voucherului primit pentru a revendica IDG Coins.</p>
                <p className="text-[10px] text-zinc-600 font-mono mt-1">Format: IDG-XXXX-XXXX-XXXX-XXXX</p>
              </div>

              {error && (
                <div className="flex items-center gap-2 p-3 rounded bg-rose-500/10 border border-rose-500/30 text-rose-400 text-xs">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <label className="text-[10px] font-chakra font-black uppercase tracking-widest text-zinc-500">Cod PIN Voucher</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm font-mono font-bold">IDG-</span>
                  <input
                    type="text"
                    value={pin}
                    onChange={handleChange}
                    onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
                    placeholder="XXXX-XXXX-XXXX-XXXX"
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-3 py-3 pl-12 font-mono text-base text-white tracking-widest focus:outline-none focus:border-violet-500 text-center transition-all"
                    autoFocus
                  />
                </div>
              </div>

              <button
                onClick={handleRedeem}
                disabled={loading || pin.replace(/-/g, "").length < 16}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-chakra font-black text-sm uppercase tracking-wider text-black transition-all hover:brightness-110 disabled:opacity-40"
                style={{ background: "var(--tf-primary)" }}
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
                {loading ? "Se verifică..." : "Revendică Voucher"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
